import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  ListSalesHistoryTransfersInput,
  ListSalesHistoryTransfersResponse,
  PreviewSalesHistoryTransferResponse,
  SalesHistoryTransferRow,
} from '@erp/rpc';

export type {
  ListSalesHistoryTransfersResponse,
  PreviewSalesHistoryTransferResponse,
  SalesHistoryTransferRow,
};

// Read-time copy/transfer rules touching a product or a warehouse on EITHER
// side — the product-detail rules list. `includeRevoked` brings the soft-revoked
// rows back so the panel can show a rule's history, not just its current state.
export function useSalesHistoryTransfers(
  input: ListSalesHistoryTransfersInput,
  enabled = true,
) {
  return useQuery<ListSalesHistoryTransfersResponse>({
    ...orpc.procurement.salesHistoryTransfers.list.queryOptions({ input }),
    enabled,
  });
}

// A created/revoked rule changes what demand the engine reads at both
// coordinates: the sales-history series on the product page and every
// suggestion the board derived from it. Invalidate the rule list plus the
// replenishment tree — `getProductSalesHistory` and the need/product-insights
// reads all hang off `replenishmentSuggestions`, so its root key covers the
// charts too. Same rationale as useDemandOverrides.
function invalidateAfterMutation(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  void queryClient.invalidateQueries({
    queryKey: orpc.procurement.salesHistoryTransfers.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.procurement.replenishmentSuggestions.key(),
  });
}

export function useCreateSalesHistoryTransfer() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.salesHistoryTransfers.create.mutationOptions({
      onSuccess: () => invalidateAfterMutation(queryClient),
    }),
  );
}

export function useRevokeSalesHistoryTransfer() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.salesHistoryTransfers.revoke.mutationOptions({
      onSuccess: () => invalidateAfterMutation(queryClient),
    }),
  );
}

/**
 * Preview a candidate rule without saving it.
 *
 * A mutation rather than a query even though it reads nothing: the dialog gates
 * "Create" behind an explicit Preview click, so the call has to be imperative
 * and its result has to survive the form being edited afterwards (the buyer
 * re-previews rather than watching a live series flicker). It writes nothing,
 * so it needs no invalidation.
 */
export function usePreviewSalesHistoryTransfer() {
  return useMutation(
    orpc.procurement.salesHistoryTransfers.preview.mutationOptions(),
  );
}
